import { AnimatePresence, motion } from 'framer-motion';
import noop from 'lodash/noop';
import type { ChangeEvent, CSSProperties, FocusEvent, FunctionComponent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconArrowUp, IconExpand45, MicrophoneIcon } from '~/ui/core/icons';
import { Textarea } from '~/ui/core/textarea/Textarea';
import { theme } from '~/ui/theme/cobrainer-theme';

import { COMPOSER_TRANSITION } from '../constants';
import type { UseChatTranscriptionReturn } from '../hooks/use-chat-transcription';
import { useDismissOnOutsideMousedown } from '../hooks/use-dismiss-on-outside-mousedown';
import { useScrollDirection } from '../hooks/use-scroll-direction';
import { RecordingOverlay } from './RecordingOverlay';

export const PILL_COMPOSER_WIDTH_PX = 432;

const DEFAULT_BORDER_COLOR = 'var(--color-gray-300)';

const COLLAPSED_BG = theme.colors.white[200];
const COLLAPSED_BG_RECORDING = 'rgb(254, 242, 242)';
const EXPANDED_BG = theme.colors.white[200];

const PILL_SHADOW = theme.shadows.medium;
const PILL_BORDER_COLOR = 'transparent';

const PILL_BORDER_RADIUS = theme.borderRadius['4xl'];
const EXPANDED_BORDER_RADIUS = theme.borderRadius.xl;

const PILL_PADDING = { paddingTop: 10, paddingRight: 12, paddingBottom: 10, paddingLeft: 20 } as const;
const EXPANDED_PADDING = { paddingTop: 6, paddingRight: 10, paddingBottom: 8, paddingLeft: 10 } as const;

const PILL_TRANSITION = COMPOSER_TRANSITION;

// Deliberately NOT using framer-motion `layout` here — the composer cross-fades
// against the tile pill (no shared layoutId morph). `layout` would re-animate
// on unrelated dock reflows (e.g. sidepanel added), making the composer lag.
const CONTAINER_BLOOM_TRANSITION = { duration: 0.22, ease: [0.215, 0.61, 0.355, 1] } as const;

const COLLAPSED_DIMMED_OPACITY = 0.55;
const COLLAPSED_ACTIVE_OPACITY = 1;
const COLLAPSED_OPACITY_TRANSITION = { duration: 0.18, ease: 'easeOut' as const };

const PILL_TEXTAREA_ROWS = 1;
const PILL_TEXTAREA_MAX_ROWS = 6;
const EXPANDED_TEXTAREA_ROWS = 3;
const EXPANDED_TEXTAREA_MAX_ROWS = 8;

const PILL_TEXTAREA_CLASS =
    'text-sm font-medium leading-[20px] tracking-[-0.28px] text-gray-900 placeholder:overflow-hidden placeholder:text-ellipsis placeholder:whitespace-nowrap placeholder:text-gray-600';
const EXPANDED_TEXTAREA_CLASS =
    'text-sm font-medium leading-[20px] tracking-[-0.28px] text-black-700 placeholder:text-gray-400 flex-1';

const resolveCollapsedBackground = (hideInput: boolean): string => (hideInput ? COLLAPSED_BG_RECORDING : COLLAPSED_BG);

const computeContainerAnimate = (isExpanded: boolean, hideInput: boolean) => ({
    backgroundColor: isExpanded ? EXPANDED_BG : resolveCollapsedBackground(hideInput),
    borderColor: isExpanded ? DEFAULT_BORDER_COLOR : PILL_BORDER_COLOR,
    borderRadius: isExpanded ? EXPANDED_BORDER_RADIUS : PILL_BORDER_RADIUS,
    borderWidth: isExpanded ? 1 : 0,
    boxShadow: isExpanded ? 'none' : PILL_SHADOW,
});

// Padding on inner row (not container) so `topSlot` can span pill edge-to-edge
// with its own divider.
const computeInnerAnimate = (isExpanded: boolean) => (isExpanded ? EXPANDED_PADDING : PILL_PADDING);

// Radix-portaled surfaces treated as extensions of the pill (not outside clicks).
const SPAWNED_OVERLAY_SELECTOR = [
    '[role="dialog"]',
    '[role="alertdialog"]',
    '[role="menu"]',
    '[role="listbox"]',
    '[data-radix-popper-content-wrapper]',
].join(',');

const isWithinSpawnedOverlay = (target: Element): boolean => target.closest(SPAWNED_OVERLAY_SELECTOR) != null;

const CONTAINER_STATIC_STYLE: CSSProperties = {
    borderStyle: 'solid',
};

// Expanded pill fills the chat card; collapsed keeps the fixed 432px
// footprint required by the floating-tile morph layoutId.
const resolveContainerWidth = (isExpanded: boolean): CSSProperties['width'] =>
    isExpanded ? '100%' : PILL_COMPOSER_WIDTH_PX;

type ComposerTextareaProps = {
    readonly draft: string;
    readonly inputTransitionStyle: CSSProperties;
    readonly isExpanded: boolean;
    readonly onDraftChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    readonly onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
};

const ComposerTextarea: FunctionComponent<ComposerTextareaProps> = ({
    draft,
    inputTransitionStyle,
    isExpanded,
    onDraftChange,
    onKeyDown,
}) => {
    const translate = useTranslate();
    const wrapperClass = isExpanded
        ? 'w-full min-w-0 basis-full order-[-1] flex-1 flex self-center'
        : 'min-w-0 flex-1 flex align-center justify-center';
    const textareaClass = isExpanded ? EXPANDED_TEXTAREA_CLASS : PILL_TEXTAREA_CLASS;
    const textareaStyle = isExpanded ? inputTransitionStyle : { marginBottom: 2, ...inputTransitionStyle };

    return (
        <motion.div className={wrapperClass} transition={PILL_TRANSITION}>
            <Textarea
                autoResize={true}
                aria-label={translate('chat.composer-placeholder')}
                className={textareaClass}
                maxRows={isExpanded ? EXPANDED_TEXTAREA_MAX_ROWS : PILL_TEXTAREA_MAX_ROWS}
                placeholder={translate('chat.composer-placeholder')}
                rows={isExpanded ? EXPANDED_TEXTAREA_ROWS : PILL_TEXTAREA_ROWS}
                style={textareaStyle}
                value={draft}
                variant="ghost"
                onChange={onDraftChange}
                onKeyDown={onKeyDown}
            />
        </motion.div>
    );
};

type ComposerButtonsProps = {
    readonly hasDraft: boolean;
    readonly idleSlot?: ReactNode;
    readonly inputTransitionStyle: CSSProperties;
    readonly isExpanded: boolean;
    readonly isOpen: boolean;
    readonly isStreaming: boolean;
    readonly transcription: UseChatTranscriptionReturn;
    readonly draft: string;
    readonly onOpen?: () => void;
    readonly onSubmit?: () => void;
};

const ComposerButtons: FunctionComponent<ComposerButtonsProps> = ({
    draft,
    hasDraft,
    idleSlot,
    inputTransitionStyle,
    isExpanded,
    isOpen,
    isStreaming,
    transcription,
    onOpen,
    onSubmit,
}) => {
    const translate = useTranslate();
    const className = isExpanded ? 'flex shrink-0 items-center gap-xs ml-auto' : 'flex shrink-0 items-center gap-m';
    const isActionDisabled = isStreaming || (hasDraft && !draft.trim());

    return (
        <div className={className} style={inputTransitionStyle}>
            {!isExpanded && !isOpen && idleSlot}
            {!isExpanded && onOpen ? (
                <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Button
                        aria-label={translate('chat.expand')}
                        iconOnly={true}
                        size="sm"
                        variant="secondary"
                        // Suppress focus-on-mousedown so clicking expand goes straight
                        // to the expanded chat without a flash of topSlot opening.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={onOpen}
                    >
                        <IconExpand45 size={12} style={{ transform: 'rotate(270deg)' }} />
                    </Button>
                </motion.div>
            ) : null}
            <motion.div transition={PILL_TRANSITION}>
                <Button
                    aria-label={hasDraft ? translate('chat.send') : translate('chat.record')}
                    disabled={isActionDisabled}
                    className="hover:opacity-100 disabled:opacity-40"
                    iconOnly={true}
                    size="sm"
                    type="button"
                    variant="primary"
                    onClick={hasDraft ? (onSubmit ?? noop) : transcription.toggleRecording}
                >
                    {hasDraft ? <IconArrowUp size={12} aria-hidden={true} /> : <MicrophoneIcon />}
                </Button>
            </motion.div>
        </div>
    );
};

type PillChatComposerProps = {
    readonly draft: string;
    readonly isExpanded: boolean;
    /** When true, the send/record action is disabled (a turn is in-flight). */
    readonly isStreaming?: boolean;
    readonly transcription: UseChatTranscriptionReturn;
    readonly onDraftChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    readonly onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    readonly onOpen?: () => void;
    // Open is sticky across blurs — only closes on explicit gestures (click outside,
    // scroll down). Lets parents render open-driven affordances (e.g. focus gradient).
    readonly onOpenChange?: (open: boolean) => void;
    readonly onSubmit?: () => void;
    readonly topSlot?: ReactNode;
    /** Rendered inline in the collapsed pill, left of the action buttons. */
    readonly idleSlot?: ReactNode;
    /**
     * Incrementing counter — every change opens the topSlot. Lets parents
     * imperatively reveal the pill without taking over the open/close state.
     */
    readonly openRequestSignal?: number;
    /** When set, replaces the textarea + send/record buttons row entirely. */
    readonly inputOverride?: ReactNode;
};

export const PillChatComposer: FunctionComponent<PillChatComposerProps> = ({
    draft,
    isExpanded,
    isStreaming = false,
    transcription,
    onDraftChange,
    onKeyDown,
    onOpen,
    onOpenChange,
    onSubmit,
    topSlot,
    idleSlot,
    openRequestSignal,
    inputOverride,
}) => {
    const hasDraft = draft.trim().length > 0;
    const hideInput = transcription.isRecording || transcription.isTranscribing;
    // Fade input row out (keep mounted) so RecordingOverlay owns the surface
    // while preserving draft/focus state across a recording round-trip.
    const inputTransitionStyle: CSSProperties = {
        opacity: hideInput ? 0 : 1,
        pointerEvents: hideInput ? 'none' : 'auto',
        transition: 'opacity 180ms ease',
    };

    // Callback ref so observation re-attaches if the topSlot wrapper remounts.
    // Tween targets a measured px height — framer's `height: 'auto'` resolves
    // mid-tween but settles to CSS auto, producing a sub-pixel snap.
    const [topSlotNode, setTopSlotNode] = useState<HTMLDivElement | null>(null);
    const [topSlotHeight, setTopSlotHeight] = useState(0);

    useEffect(() => {
        if (topSlotNode == null) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            if (entry != null) {
                setTopSlotHeight(entry.contentRect.height);
            }
        });
        observer.observe(topSlotNode);
        return () => observer.disconnect();
    }, [topSlotNode]);

    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    // Sticky across textarea blur — topSlot button clicks must not dismiss it.
    // Opens on first focus. Closes only on click-outside or scroll-down.
    const [isOpen, setIsOpen] = useState(false);
    // Gates scroll-up auto-reopen so fresh page loads don't surface topSlot
    // uninvited. Reset on click-outside to also disarm the reveal.
    const hasOpenedOnceRef = useRef(false);

    // External requests to expand the pill (e.g. clicking an idle-slot
    // affordance). Each new signal value reopens; skipped on first mount so
    // a stale initial value can't surface the topSlot uninvited.
    const lastOpenRequestRef = useRef(openRequestSignal);
    useEffect(() => {
        if (openRequestSignal === undefined || openRequestSignal === lastOpenRequestRef.current) {
            return;
        }
        lastOpenRequestRef.current = openRequestSignal;
        setIsOpen(true);
        hasOpenedOnceRef.current = true;
    }, [openRequestSignal]);

    // Open once the entry transition has settled — opening earlier flashes the
    // topSlot while the pill is still moving into place.
    useEffect(() => {
        const timeout = setTimeout(() => {
            setIsOpen(true);
            hasOpenedOnceRef.current = true;
        }, 400);
        return () => clearTimeout(timeout);
    }, []);

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    const handleOutsideDismiss = useCallback(() => {
        setIsOpen(false);
        hasOpenedOnceRef.current = false;
    }, []);

    useDismissOnOutsideMousedown({
        active: isOpen,
        containerRef,
        isInsideAllowedExtension: isWithinSpawnedOverlay,
        onDismiss: handleOutsideDismiss,
    });

    const handleScrollDown = useCallback(() => setIsOpen(false), []);
    const handleScrollUp = useCallback(() => {
        if (hasOpenedOnceRef.current) {
            setIsOpen(true);
        }
    }, []);

    useScrollDirection({ onScrollDown: handleScrollDown, onScrollUp: handleScrollUp });

    const showTopSlot = topSlot != null && (isExpanded || isOpen) && !isStreaming;
    const isDimmed = !(isOpen && showTopSlot) && !isExpanded && !hideInput && !hasDraft && !isHovered && !isFocused;

    const innerClass = isExpanded ? 'relative flex w-full items-end' : 'relative flex items-end';
    const innerStyle = isExpanded ? { gap: 6 } : { gap: 24 };

    return (
        <motion.div
            ref={containerRef}
            className="font-sans"
            initial={false}
            animate={{
                ...computeContainerAnimate(isExpanded, hideInput),
                opacity: isDimmed ? COLLAPSED_DIMMED_OPACITY : COLLAPSED_ACTIVE_OPACITY,
            }}
            transition={{
                ...CONTAINER_BLOOM_TRANSITION,
                opacity: COLLAPSED_OPACITY_TRANSITION,
            }}
            style={{ ...CONTAINER_STATIC_STYLE, width: resolveContainerWidth(isExpanded) }}
            onBlur={(event: FocusEvent<HTMLDivElement>) => {
                const next = event.relatedTarget as Node | null;
                if (next != null && containerRef.current?.contains(next) === true) {
                    return;
                }
                setIsFocused(false);
            }}
            onFocus={(event: FocusEvent<HTMLDivElement>) => {
                event.stopPropagation();
                setIsFocused(true);
                setIsOpen(true);
                hasOpenedOnceRef.current = true;
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {topSlot != null && (
                <motion.div
                    className="overflow-hidden"
                    data-element="composer-top-slot"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                        height: showTopSlot ? topSlotHeight : 0,
                        opacity: showTopSlot ? 1 : 0,
                    }}
                    transition={CONTAINER_BLOOM_TRANSITION}
                >
                    <div ref={setTopSlotNode} className="border-b border-gray-200">
                        {topSlot}
                    </div>
                </motion.div>
            )}
            <motion.div
                className={innerClass}
                initial={false}
                animate={computeInnerAnimate(isExpanded)}
                transition={CONTAINER_BLOOM_TRANSITION}
                style={innerStyle}
            >
                <AnimatePresence initial={false}>
                    {transcription.showOverlay && (
                        <RecordingOverlay
                            key="recording-overlay"
                            getAmplitude={transcription.getAmplitude}
                            isFadingOut={transcription.overlayFadingOut}
                            isTranscribing={transcription.isTranscribing}
                            recordingSeconds={transcription.recordingSeconds}
                            onStopRecording={transcription.stopRecording}
                        />
                    )}
                    {inputOverride != null ? (
                        <div key="composer-input-override" className="w-full min-w-0">
                            {inputOverride}
                        </div>
                    ) : (
                        <>
                            <ComposerTextarea
                                draft={draft}
                                key="composer-textarea"
                                inputTransitionStyle={inputTransitionStyle}
                                isExpanded={isExpanded}
                                onDraftChange={onDraftChange}
                                onKeyDown={onKeyDown}
                            />
                            <ComposerButtons
                                draft={draft}
                                key="composer-buttons"
                                hasDraft={hasDraft}
                                idleSlot={idleSlot}
                                inputTransitionStyle={inputTransitionStyle}
                                isExpanded={isExpanded}
                                isOpen={isOpen}
                                isStreaming={isStreaming}
                                transcription={transcription}
                                onOpen={onOpen}
                                onSubmit={onSubmit}
                            />
                        </>
                    )}
                </AnimatePresence>
            </motion.div>
        </motion.div>
    );
};
